import { Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil, tap } from 'rxjs/operators';

import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCallSign, SnUser } from '@lib/api/entities';
import { SnCallSignsAPI } from '@lib/api/services';
import { snFadeInDown } from '@lib/common/animations';
import { SnDataListComponent } from '@lib/ui/data-list';

@Component({
  selector: 'sn-call-sign-dialog',
  templateUrl: './call-sign-dialog.component.html',
  styleUrls: ['./call-sign-dialog.component.scss'],
  animations: [snFadeInDown],
})
export class CallSignDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: true })
  public readonly list: SnDataListComponent<SnCallSign>;
  public readonly loadData = this._loadData.bind(this);
  public loading: boolean = true;
  public selected: boolean = true;
  @Input()
  public user: SnUser;
  public callSignList: SnCallSign[] = [];
  public callSignControl: UntypedFormControl;
  @Input()
  public callSign: any;
  private readonly _destroy$ = new Subject<void>();

  private _search: string = '';
  constructor(
    private readonly _callSignService: SnCallSignsAPI,
    private readonly _dialogRef: MatDialogRef<CallSignDialogComponent>,
  ) {}

  public ngOnInit(): void {
    this.callSignControl = new UntypedFormControl('', Validators.required);

    this.callSignControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((value) => {
        this._loadCallSigns(value);
      });
  }

  public onSearchValueChanged(value: string): void {
    this._search = value;
    this.list.reference.reload();
  }

  public onCallSignSelected(callSign: SnCallSign): void {
    this.callSign = callSign.name;
  }

  public onSubmit(): void {
    this._dialogRef.close(this.callSign);
  }

  public close(): void {
    this._dialogRef.close(false);
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _loadData(params: ISnListQueryParameters): Observable<ISnListResponse<SnCallSign>> {
    if (this._search) {
      params.search = this._search;
    }
    return this._callSignService.getCallSignList({
      ...params,
    });
  }

  private _loadCallSigns(search: string): void {
    this._callSignService
      .getCallSignList({ search })
      .pipe(
        map((response: any) => response.results),
        tap((callSigns) => {
          if (!callSigns.length) {
            this.callSignControl.setErrors({ invalid: true });
          }
        }),
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: (callSigns) => (this.callSignList = callSigns),
      });
  }
}
